import React, { useEffect, useState } from "react";
import { z } from "zod";
import { toast } from "sonner";
import { Loader2, Package } from "lucide-react";
import { api, extractApiError } from "@/lib/api";
import { checkoutReturnFields } from "@/lib/tenantPath";
import { checkoutErrorMessage } from "@/lib/checkoutErrors";
import { useLanguage, useT } from "@/lib/LanguageContext";
import { pickLocalized } from "../../i18n/pickLocalized";
import { formatMoneyIntl } from "../../lib/formatMoney";
import { currentRef } from "../../lib/affiliateRef";
// --- crm-module BEGIN C3.04 (marketplace attribution -- write side) ---
import { currentPromotionRef } from "../../lib/promotionRef";
// --- crm-module END ---
import ThemedPageLayout from "@/templates/ThemedPageLayout";
import useFieldErrors from "../../hooks/useFieldErrors";
import FieldError, { invalidInputClass } from "../../components/ui/FieldError";
import { emailField, requiredField } from "../../lib/validation";
import MarketingOptIn from "../../components/consent/MarketingOptIn";

// The canonical recipe (lib/validation.js): every message is an i18n KEY resolved
// through the CALLER's own `t`, never prose baked into the schema.
const SCHEMA = z.object({ name: requiredField(200), email: emailField });

/**
 * Public session-pack purchase (contract C2.03: `GET /api/public/packs`,
 * `POST /api/checkout/pack {pack_id, email, name, origin_url, promo_code?}`). A GUEST
 * flow, deliberately unlike the storefront's product/gift-card buttons — the route
 * carries the buyer's name and email itself (`crm.api.fulfil_pack` needs them to
 * `ensure_client`), so it never gates on a signed-in session the way `Boutique.buy()`
 * does (BUG-020 precedent: the checkout endpoint decides who the buyer is from what the
 * payload says, not from `useRequireAuth`).
 */
export default function PackPurchase() {
  const { lang } = useLanguage();
  const t = useT("public");
  const tc = useT("common");
  const [packs, setPacks] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [promoCode, setPromoCode] = useState("");
  // The OPTIONAL "send me news and offers" box — unticked until the buyer ticks it
  // (CASL); recorded by the server only once the pack is paid.
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [busy, setBusy] = useState(false);
  const { fieldErrors, validate, clearFieldError } = useFieldErrors();

  useEffect(() => {
    let alive = true;
    api.get("/public/packs")
      .then(({ data }) => { if (alive) setPacks(Array.isArray(data) ? data : (data?.rows || [])); })
      .catch((e) => { if (alive) setLoadError(e); });
    return () => { alive = false; };
  }, []);

  const buy = async () => {
    if (!selected) return;
    if (validate(SCHEMA, { name, email })) return;
    setBusy(true);
    try {
      const { data } = await api.post("/checkout/pack", {
        pack_id: selected.id,
        email,
        name,
        origin_url: window.location.origin,
        ...checkoutReturnFields("/merci/forfait", "/forfaits"),
        locale: lang,
        ...(promoCode ? { promo_code: promoCode } : {}),
        ref: currentRef(),
        // --- crm-module BEGIN C3.04 (marketplace attribution -- write side) ---
        promotion_id: currentPromotionRef() || undefined,
        // --- crm-module END ---
        marketing_opt_in: marketingOptIn,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(checkoutErrorMessage(e, t, t("packs.toast.error")));
      setBusy(false);
    }
  };

  return (
    <ThemedPageLayout>
      <div className="max-w-3xl mx-auto px-4 py-12" data-testid="pack-purchase">
        <h1 className="font-display text-4xl">{t("packs.title")}</h1>
        <p className="text-eh-muted text-sm mt-2">{t("packs.subtitle")}</p>

        {loadError && <p className="mt-6 text-sm text-red-600">{extractApiError(loadError, t("packs.toast.loadError"))}</p>}
        {!loadError && packs === null && <Loader2 className="mt-8 animate-spin text-eh-muted" size={20} />}
        {packs && packs.length === 0 && <p className="mt-8 text-sm text-eh-muted">{t("packs.empty")}</p>}

        {packs && packs.length > 0 && (
          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {packs.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setSelected(p)}
                data-testid={`pack-purchase-option-${p.id}`}
                className={`text-left rounded-2xl border p-5 transition ${selected?.id === p.id ? "border-eh-ink bg-eh-cream" : "border-eh-border-soft hover:bg-eh-cream"}`}
              >
                <Package size={18} className="text-eh-muted" />
                <div className="mt-2 font-display text-xl">{p.name}</div>
                <div className="text-sm text-eh-muted">{t("packs.sessions", { n: p.session_count })}</div>
                <div className="mt-1 font-semibold">{formatMoneyIntl(p.price_cents, p.currency, lang)}</div>
                {p.service_name && <div className="mt-1 text-xs text-eh-muted">{pickLocalized(p, "service_name", lang)}</div>}
              </button>
            ))}
          </div>
        )}

        {selected && (
          <div className="mt-8 space-y-3 max-w-md">
            <div>
              <input
                placeholder={t("packs.namePh")}
                value={name}
                onChange={(e) => { setName(e.target.value); clearFieldError("name"); }}
                className={`w-full bg-white border border-eh-border-soft rounded-xl px-3 py-2.5 text-sm ${fieldErrors.name ? invalidInputClass : ""}`}
                data-testid="pack-purchase-name"
              />
              <FieldError message={fieldErrors.name ? tc(fieldErrors.name) : ""} testId="pack-purchase-name-error" />
            </div>
            <div>
              <input
                placeholder={t("packs.emailPh")}
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); clearFieldError("email"); }}
                className={`w-full bg-white border border-eh-border-soft rounded-xl px-3 py-2.5 text-sm ${fieldErrors.email ? invalidInputClass : ""}`}
                data-testid="pack-purchase-email"
              />
              <FieldError message={fieldErrors.email ? tc(fieldErrors.email) : ""} testId="pack-purchase-email-error" />
            </div>
            <input
              placeholder={t("packs.promoPh")}
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
              className="w-full bg-white border border-eh-border-soft rounded-xl px-3 py-2.5 text-sm"
              data-testid="pack-purchase-promo"
            />
            <MarketingOptIn form="pack" checked={marketingOptIn} onChange={setMarketingOptIn} disabled={busy} />
            <button onClick={buy} disabled={busy} className="btn-primary text-sm disabled:opacity-60" data-testid="pack-purchase-submit">
              {busy ? <Loader2 size={14} className="animate-spin" /> : null} {t("packs.buy", { name: selected.name })}
            </button>
          </div>
        )}
      </div>
    </ThemedPageLayout>
  );
}
